import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import {
  RefreshCw, TrendingUp, Trophy, Users as UsersIcon,
  BookOpen, Flame, BarChart3, Crown
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { API_BASE, getAdminToken } from './adminUtils';

const fmtINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); }
  catch { return iso; }
};

const RankMedal = ({ rank }) => {
  if (rank === 1) return <span className="text-2xl" data-testid="rank-gold">🥇</span>;
  if (rank === 2) return <span className="text-2xl" data-testid="rank-silver">🥈</span>;
  if (rank === 3) return <span className="text-2xl" data-testid="rank-bronze">🥉</span>;
  return <span className="text-slate-400 font-semibold w-8 inline-block text-center">{rank}</span>;
};

const CHART_TOOLTIP_STYLE = {
  backgroundColor: 'rgba(15,23,42,0.95)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: '8px',
  color: '#fff'
};

export const AnalyticsTab = () => {
  const [growth, setGrowth] = useState(null);
  const [popular, setPopular] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(false);

  const [growthDays, setGrowthDays] = useState(30);
  const [popularDays, setPopularDays] = useState(null); // null = all-time
  const [revenueDays, setRevenueDays] = useState(30);
  const [lbCourse, setLbCourse] = useState('ALL');
  const [lbPeriod, setLbPeriod] = useState('all');

  const authHeaders = () => {
    const t = getAdminToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = authHeaders();
      const popularUrl = popularDays
        ? `${API_BASE}/api/admin/analytics/popular-courses?days=${popularDays}`
        : `${API_BASE}/api/admin/analytics/popular-courses`;

      const [gRes, pRes, rRes, lRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/analytics/user-growth?days=${growthDays}`, { headers }),
        fetch(popularUrl, { headers }),
        fetch(`${API_BASE}/api/admin/analytics/revenue-trend?days=${revenueDays}`, { headers }),
        fetch(`${API_BASE}/api/admin/analytics/leaderboard?limit=50&course=${lbCourse}&period=${lbPeriod}`, { headers })
      ]);
      if (gRes.ok) setGrowth(await gRes.json());
      if (pRes.ok) setPopular(await pRes.json());
      if (rRes.ok) setRevenue(await rRes.json());
      if (lRes.ok) setLeaderboard(await lRes.json());
    } catch (e) {
      console.error('Analytics fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [growthDays, popularDays, revenueDays, lbCourse, lbPeriod]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const topCourses = popular?.courses?.slice(0, 10) || [];

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-amber-400" />
            Analytics Dashboard
          </h2>
          <p className="text-sm text-slate-400 mt-1">Real-time insights · Tamper-proof metrics from VM-verified activity</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAll}
          disabled={loading}
          className="border-white/20 text-white hover:bg-white/10"
          data-testid="analytics-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-300">Total Users</p>
                <p className="text-2xl font-bold text-white" data-testid="stat-total-users">{growth?.total_users ?? '—'}</p>
                <p className="text-xs text-blue-400 mt-1">+{growth?.new_in_period ?? 0} in {growthDays}d</p>
              </div>
              <UsersIcon className="w-8 h-8 text-blue-400/60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-300">Enrollments</p>
                <p className="text-2xl font-bold text-white" data-testid="stat-total-enrollments">{popular?.total_enrollments ?? '—'}</p>
                <p className="text-xs text-emerald-400 mt-1">{popular?.courses?.length ?? 0} courses</p>
              </div>
              <BookOpen className="w-8 h-8 text-emerald-400/60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-300">Revenue ({revenueDays}d)</p>
                <p className="text-2xl font-bold text-white" data-testid="stat-revenue">{fmtINR(revenue?.total_revenue_inr || 0)}</p>
                <p className="text-xs text-amber-400 mt-1">INR equivalent</p>
              </div>
              <TrendingUp className="w-8 h-8 text-amber-400/60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-300">Active Learners</p>
                <p className="text-2xl font-bold text-white" data-testid="stat-active-learners">{leaderboard?.total_students ?? '—'}</p>
                <p className="text-xs text-purple-400 mt-1">with verified labs</p>
              </div>
              <Trophy className="w-8 h-8 text-purple-400/60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Growth Chart */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <UsersIcon className="w-5 h-5 text-blue-400" />User Growth
            </CardTitle>
            <select
              value={growthDays}
              onChange={e => setGrowthDays(Number(e.target.value))}
              className="bg-white/10 border border-white/20 text-white rounded-md px-3 py-1.5 text-sm"
              data-testid="growth-period-select"
            >
              <option value={7} className="bg-slate-800">7 days</option>
              <option value={30} className="bg-slate-800">30 days</option>
              <option value={90} className="bg-slate-800">90 days</option>
              <option value={180} className="bg-slate-800">180 days</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {growth?.series?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={growth.series}>
                <defs>
                  <linearGradient id="gradCumulative" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradNew" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelFormatter={fmtDate} />
                <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                <Area type="monotone" dataKey="cumulative" stroke="#10b981" fill="url(#gradCumulative)" name="Total Users" strokeWidth={2} />
                <Area type="monotone" dataKey="new_users" stroke="#3b82f6" fill="url(#gradNew)" name="New Signups" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-slate-400">No data</div>
          )}
        </CardContent>
      </Card>

      {/* Popular Courses + Revenue Trend side-by-side on wide screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Courses */}
        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-emerald-400" />Popular Courses
              </CardTitle>
              <select
                value={popularDays ?? ''}
                onChange={e => setPopularDays(e.target.value ? Number(e.target.value) : null)}
                className="bg-white/10 border border-white/20 text-white rounded-md px-3 py-1.5 text-sm"
                data-testid="popular-period-select"
              >
                <option value="" className="bg-slate-800">All time</option>
                <option value={7} className="bg-slate-800">7 days</option>
                <option value={30} className="bg-slate-800">30 days</option>
                <option value={90} className="bg-slate-800">90 days</option>
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {topCourses.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={topCourses} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                  <YAxis type="category" dataKey="course_id" stroke="#94a3b8" fontSize={11} width={90} />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE}
                    formatter={(v, name) => name === 'revenue_inr' ? fmtINR(v) : v} />
                  <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                  <Bar dataKey="enrollments" fill="#10b981" name="Enrollments" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-slate-400">No data</div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Trend */}
        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-400" />Revenue Trend
              </CardTitle>
              <select
                value={revenueDays}
                onChange={e => setRevenueDays(Number(e.target.value))}
                className="bg-white/10 border border-white/20 text-white rounded-md px-3 py-1.5 text-sm"
                data-testid="revenue-period-select"
              >
                <option value={7} className="bg-slate-800">7 days</option>
                <option value={30} className="bg-slate-800">30 days</option>
                <option value={90} className="bg-slate-800">90 days</option>
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {revenue?.series?.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={revenue.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE}
                    labelFormatter={fmtDate}
                    formatter={(v) => fmtINR(v)} />
                  <Line type="monotone" dataKey="revenue_inr" stroke="#f59e0b" strokeWidth={2}
                    dot={{ fill: '#f59e0b', r: 3 }} name="Revenue (INR)" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-slate-400">No data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Leaderboard */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" />Student Leaderboard
              <span className="text-xs text-slate-400 font-normal ml-2">
                Score = labs × 100 + total points
              </span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <select
                value={lbCourse}
                onChange={e => setLbCourse(e.target.value)}
                className="bg-white/10 border border-white/20 text-white rounded-md px-3 py-1.5 text-sm"
                data-testid="leaderboard-course-select"
              >
                <option value="ALL" className="bg-slate-800">All Courses</option>
                {leaderboard?.available_courses?.map(c => (
                  <option key={c} value={c} className="bg-slate-800">{c}</option>
                ))}
              </select>
              <select
                value={lbPeriod}
                onChange={e => setLbPeriod(e.target.value)}
                className="bg-white/10 border border-white/20 text-white rounded-md px-3 py-1.5 text-sm"
                data-testid="leaderboard-period-select"
              >
                <option value="all" className="bg-slate-800">All time</option>
                <option value="7" className="bg-slate-800">7 days</option>
                <option value="30" className="bg-slate-800">30 days</option>
                <option value="90" className="bg-slate-800">90 days</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {leaderboard?.leaderboard?.length > 0 ? (
            <div className="rounded-xl border border-white/10 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-slate-300 w-16">Rank</TableHead>
                    <TableHead className="text-slate-300">Student</TableHead>
                    <TableHead className="text-slate-300 text-right">Labs Done</TableHead>
                    <TableHead className="text-slate-300 text-right">Score</TableHead>
                    <TableHead className="text-slate-300 text-right">Courses</TableHead>
                    <TableHead className="text-slate-300 text-right">Streak</TableHead>
                    <TableHead className="text-slate-300 text-right">Rank Pts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboard.leaderboard.map((entry) => (
                    <TableRow key={entry.user_id} className="border-white/10 hover:bg-white/5"
                      data-testid={`leaderboard-row-${entry.rank}`}>
                      <TableCell><RankMedal rank={entry.rank} /></TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-white font-medium">{entry.user_name}</span>
                          <span className="text-xs text-slate-500">{entry.user_email}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                          {entry.labs_completed}/{entry.labs_attempted}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-amber-300 font-semibold">
                        {entry.total_score.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-slate-300">
                        {entry.courses_attempted}
                      </TableCell>
                      <TableCell className="text-right">
                        {entry.streak_days > 0 ? (
                          <span className="inline-flex items-center gap-1 text-orange-400 font-semibold">
                            <Flame className="w-4 h-4" />{entry.streak_days}d
                          </span>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-white font-bold">
                        {entry.rank_score.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              {loading ? 'Loading...' : 'No students found for this filter'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
